import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Crown, Gem, Frown, Loader, History } from 'lucide-react'
import { toast } from 'react-hot-toast'
import useAppStore from '../../../store/useAppStore'
import useRoyaleStore from '../store/useRoyaleStore'
import RoyaleParticipantsShowcase from '../components/RoyaleParticipantsShowcase'

const RoyaleHistory = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { loadHistory } = useRoyaleStore()
  const { user } = useAppStore()

  const [activeTab, setActiveTab] = useState('all')
  const [allRounds, setAllRounds] = useState([])
  const [myRounds, setMyRounds] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const currentUserName = useMemo(() => {
    if (!user) return ''
    const fullName = `${user.firstname || user.first_name || ''} ${user.lastname || user.last_name || ''}`.trim()
    return (fullName || user.username || user.name || '').trim()
  }, [user])

  const normalizeName = (value) => String(value || '').trim().toLowerCase()

  useEffect(() => {
    loadRounds()
  }, [])

  const loadRounds = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const { all, mine } = await loadHistory()
      setAllRounds(all || [])
      setMyRounds(mine || [])
    } catch (err) {
      console.error('Ошибка загрузки истории Royale:', err)
      setError(err.message || t('royale.history.loadError'))
      toast.error(t('royale.history.loadErrorToast'))
    } finally {
      setIsLoading(false)
    }
  }

  const currentRounds = activeTab === 'all' ? allRounds : myRounds
  const currentRoundsCount = currentRounds.length

  return (
    <div className="px-4 py-6 pb-16 lg:px-6 lg:py-2 lg:pb-8">
      <div className="mx-auto max-w-7xl space-y-6 lg:space-y-8">
        <div className="space-y-4 lg:space-y-6">
          <div className="relative flex items-center justify-center lg:min-h-[48px]">
            <button
              onClick={() => navigate('/royale')}
              className="absolute left-0 inline-flex items-center justify-center rounded-xl border border-white/10 bg-surface p-2.5 text-white transition-colors hover:border-accent-primary/40 hover:text-accent-primary"
            >
              <ArrowLeft className="h-5 w-5 lg:h-6 lg:w-6" />
            </button>

            <div className="flex items-center gap-3">
              <History className="w-6 h-6 text-accent-primary lg:w-8 lg:h-8" />
              <h1 className="text-2xl font-bold text-accent-primary lg:text-4xl">{t('royale.history.title')}</h1>
            </div>
          </div>

          <div className="flex justify-center gap-2 lg:hidden">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'all'
                  ? 'bg-accent-primary text-dark-primary'
                  : 'bg-surface text-text-secondary hover:bg-surface/80'
              }`}
            >
              {t('royale.history.tabs.all')}
            </button>
            <button
              onClick={() => setActiveTab('my')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'my'
                  ? 'bg-accent-primary text-dark-primary'
                  : 'bg-surface text-text-secondary hover:bg-surface/80'
              }`}
            >
              {t('royale.history.tabs.my')}
            </button>
          </div>
        </div>

        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start lg:gap-6">
          <div className="space-y-4 lg:space-y-6">
            {isLoading && (
              <div className="flex min-h-[260px] items-center justify-center rounded-2xl border border-white/10 bg-surface/60 px-4 py-12">
                <Loader className="w-8 h-8 text-accent-primary animate-spin" />
              </div>
            )}

            {!isLoading && error && (
              <div className="rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-8 text-center text-red-300 lg:px-6">
                {error}
              </div>
            )}

            {!isLoading && !error && currentRoundsCount === 0 && (
              <div className="flex min-h-[320px] flex-col items-center justify-center rounded-2xl border border-white/10 bg-surface/60 px-4 py-12">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-dark-secondary/70">
                  <Frown className="h-8 w-8 text-text-secondary" />
                </div>
                <h3 className="mb-2 text-center text-xl font-semibold text-white">{t('royale.history.emptyTitle')}</h3>
                <p className="max-w-md text-center text-text-secondary">
                  {t('royale.history.emptyDescription')}
                </p>
              </div>
            )}

            {!isLoading && !error && currentRoundsCount > 0 && (
              <div className="space-y-4 lg:grid lg:grid-cols-2 lg:gap-4 lg:space-y-0">
                {currentRounds.map((round) => {
                  const sortedUsers = [...(round.users || [])].sort((a, b) => Number(b.percent || 0) - Number(a.percent || 0))
                  const winner = sortedUsers.find((participant) => participant.win) || sortedUsers[0]
                  const winnerPercent = Number(winner?.percent || 0)
                  const formattedWinnerPercent = Number.isFinite(winnerPercent)
                    ? `${winnerPercent.toFixed(2)}%`
                    : '—'
                  const formattedWin = Number(round.total_crystal ?? round.total_bet ?? 0).toLocaleString()
                  const percentToneClass = Number.isFinite(winnerPercent) && winnerPercent < 5
                    ? 'bg-[#FFD166]/15 text-[#FFD166] border-[#FFD166]/40'
                    : 'bg-accent-primary/15 text-accent-primary border-accent-primary/30'
                  const highlightId = String(winner?.chat_id || winner?.id || winner?.name || '')
                  // Индекс победителя в массиве, который реально уходит в showcase.
                  // Нужен как надёжный fallback, когда у победителя пустые name/chat_id/id
                  const highlightIndex = winner ? sortedUsers.indexOf(winner) : -1
                  const isUserParticipant = Boolean(round?.you || round?.you_win)
                    || (currentUserName
                      && (round.users || []).some((participant) => normalizeName(participant?.name) === normalizeName(currentUserName)))
                  const participationLabel = round?.you_win ? t('royale.winner.statusWin') : t('royale.winner.statusLose')
                  const participationClassName = round?.you_win
                    ? 'border-accent-primary/60 bg-accent-primary/15 text-accent-primary'
                    : 'border-red-400/40 bg-red-500/10 text-red-200'

                  return (
                    <button
                      key={round.id}
                      onClick={() => navigate(`/royale/history/${round.id}`)}
                      className="relative w-full rounded-2xl border border-white/10 bg-surface p-4 text-left transition-all hover:border-accent-primary/40 hover:bg-surface/80 lg:p-5"
                    >
                      {isUserParticipant && (
                        <div className="mb-3">
                          <span className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-[11px] font-semibold ${participationClassName}`}>
                            {participationLabel}
                          </span>
                        </div>
                      )}

                      <div className="relative z-0 mb-4">
                        <RoyaleParticipantsShowcase
                          users={sortedUsers}
                          totalBet={Number(round.total_crystal || 0)}
                          monochrome
                          highlightId={highlightId}
                          highlightIndex={highlightIndex}
                          containerClassName="w-full aspect-square lg:aspect-[1.15/1]"
                        />
                      </div>

                      <div className="relative z-10 flex flex-col items-center gap-3 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <p className="inline-flex items-center gap-2 text-xl font-bold text-white lg:text-2xl">
                            <Gem className="w-4 h-4 text-accent-primary lg:w-5 lg:h-5" />
                            {formattedWin}
                          </p>
                        </div>

                        <span className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold ${percentToneClass}`}>
                          {formattedWinnerPercent}
                        </span>
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          <div className="mt-6 space-y-4 lg:mt-0 lg:sticky lg:top-28">
            <div className="hidden rounded-2xl border border-white/10 bg-surface/60 p-4 lg:block">
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setActiveTab('all')}
                  className={`rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
                    activeTab === 'all'
                      ? 'bg-accent-primary text-dark-primary'
                      : 'bg-dark-secondary/70 text-text-secondary hover:bg-dark-secondary hover:text-white'
                  }`}
                >
                  {t('royale.history.tabs.all')}
                </button>
                <button
                  onClick={() => setActiveTab('my')}
                  className={`rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
                    activeTab === 'my'
                      ? 'bg-accent-primary text-dark-primary'
                      : 'bg-dark-secondary/70 text-text-secondary hover:bg-dark-secondary hover:text-white'
                  }`}
                >
                  {t('royale.history.tabs.my')}
                </button>
              </div>

              <div className="mt-4 rounded-xl border border-white/10 bg-dark-secondary/40 p-4 hidden">
                <p className="text-xs uppercase tracking-[0.2em] text-text-secondary">{t('royale.history.title')}</p>
                <p className="mt-3 inline-flex items-center gap-2 text-2xl font-bold text-accent-primary">
                  <Crown className="h-5 w-5" />
                  {currentRoundsCount}
                </p>
              </div>

              <button
                onClick={() => navigate('/royale')}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-dark-secondary/70 px-4 py-3 text-sm font-semibold text-white transition-colors hover:border-accent-primary/40 hover:text-accent-primary"
              >
                <ArrowLeft className="h-4 w-4" />
                {t('royale.title')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RoyaleHistory
